import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import './authentication.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STUDENT_API_BASE_URL = process.env.REACT_APP_STUDENT_DASHBOARD_API_BASE || `${API_BASE_URL}/api/student-app`;
const AUTH_API_BASE_URL = process.env.REACT_APP_AUTH_API_BASE_URL || `${API_BASE_URL}/api/auth`;
const COLLEGE_LOOKUP_URL = process.env.REACT_APP_COLLEGE_LOOKUP_URL || `${API_BASE_URL}/api/admin-app/college-lookup/`;
const QMANAGER_ACCESS_CODE = process.env.REACT_APP_QMANAGER_ACCESS_CODE || 'qmanager';

const SignupMain = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        collegeCode: '',
        email: '',
        name: '',
        password: '',
        password2: '',
        phone: '',
        agreeToTerms: false,
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [collegeName, setCollegeName] = useState('');
    const [isCollegeLookupLoading, setIsCollegeLookupLoading] = useState(false);
    const [collegeLookupError, setCollegeLookupError] = useState('');

    useEffect(() => {
        const code = (formData.collegeCode || '').trim();

        if (!code) {
            setCollegeName('');
            setCollegeLookupError('');
            setIsCollegeLookupLoading(false);
            return;
        }

        if (code.toLowerCase() === QMANAGER_ACCESS_CODE.toLowerCase()) {
            setCollegeName('Question Manager Access');
            setCollegeLookupError('');
            setIsCollegeLookupLoading(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setIsCollegeLookupLoading(true);
            setCollegeLookupError('');

            try {
                const response = await fetch(COLLEGE_LOOKUP_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        college_id: code,
                    }),
                });

                const data = await response.json();

                if (response.ok) {
                    setCollegeName(data.college?.college_name || '');
                    setCollegeLookupError('');
                } else {
                    setCollegeName('');
                    setCollegeLookupError(data.error || 'Invalid college code.');
                }
            } catch (error) {
                setCollegeName('');
                setCollegeLookupError('Unable to verify college code right now.');
            } finally {
                setIsCollegeLookupLoading(false);
            }
        }, 350);

        return () => clearTimeout(timeoutId);
    }, [formData.collegeCode]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        const nextFormData = {
            ...formData,
            [name]: nextValue,
        };

        setFormData(nextFormData);

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Keep confirm password validation in sync while typing.
        if (name === 'password' || name === 'password2') {
            if (nextFormData.password2 && nextFormData.password !== nextFormData.password2) {
                setErrors(prev => ({ ...prev, password2: 'Passwords do not match' }));
            } else {
                setErrors(prev => ({ ...prev, password2: '' }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!formData.collegeCode.trim()) {
            newErrors.collegeCode = 'College code is required';
        } else if (!collegeName) {
            newErrors.collegeCode = 'Enter a valid college code';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Full name is required';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.password2) {
            newErrors.password2 = 'Please confirm your password';
        } else if (formData.password !== formData.password2) {
            newErrors.password2 = 'Passwords do not match';
        }

        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the Terms & Conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            swal('Validation Error', 'Please fix the errors and try again.', 'error');
            return;
        }

        setIsSubmitting(true);
        swal({
            title: 'Creating Account...',
            text: 'Please wait',
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
        });

        try {
            const normalizedCode = formData.collegeCode.trim().toLowerCase();
            const isQuestionManagerSignup = normalizedCode === QMANAGER_ACCESS_CODE.toLowerCase();

            const response = await fetch(
                isQuestionManagerSignup ? `${AUTH_API_BASE_URL}/question-manager-signup/` : `${STUDENT_API_BASE_URL}/signup/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(
                        isQuestionManagerSignup
                            ? {
                                  access_code: formData.collegeCode.trim(),
                                  email: formData.email.toLowerCase().trim(),
                                  name: formData.name.trim(),
                                  phone: formData.phone.trim(),
                                  password: formData.password,
                                  confirm_password: formData.password2,
                              }
                            : {
                                  email: formData.email.toLowerCase().trim(),
                                  college_code: formData.collegeCode.trim(),
                                  username: formData.name.trim(),
                                  password: formData.password,
                                  confirm_password: formData.password2,
                              }
                    ),
                }
            );

            const data = await response.json();

            if (response.ok) {
                swal(
                    'Success!',
                    isQuestionManagerSignup
                        ? 'Question Manager signup submitted. Wait for admin approval before login.'
                        : 'Signup submitted successfully. Please log in after approval.',
                    'success'
                );

                // Student profile signup is a request workflow; redirect to login after submit.
                setTimeout(() => {
                    navigate('/login');
                }, 1500);
            } else {
                swal.close();
                // Handle different error types
                if (data.email) {
                    setErrors(prev => ({ ...prev, email: data.email[0] || 'Email already exists' }));
                }
                if (data.college_code) {
                    setErrors(prev => ({ ...prev, collegeCode: data.college_code[0] || 'Invalid college code' }));
                }
                if (data.access_code) {
                    setErrors(prev => ({ ...prev, collegeCode: data.access_code[0] || 'Invalid access code' }));
                }
                if (data.username) {
                    setErrors(prev => ({ ...prev, name: data.username[0] || 'Username already exists' }));
                }
                if (data.name) {
                    setErrors(prev => ({ ...prev, name: data.name[0] || 'Name is invalid' }));
                }
                if (data.password) {
                    swal('Password Error', Array.isArray(data.password) ? data.password[0] : data.password, 'error');
                }
                if (data.confirm_password) {
                    setErrors(prev => ({ ...prev, password2: Array.isArray(data.confirm_password) ? data.confirm_password[0] : data.confirm_password }));
                }
                if (data.non_field_errors) {
                    swal('Registration Error', data.non_field_errors[0], 'error');
                }
                if (data.error) {
                    swal('Error', data.error, 'error');
                }
                if (!data.error && !data.email && !data.college_code && !data.access_code && !data.username && !data.name && !data.password && !data.confirm_password && !data.non_field_errors) {
                    swal('Error', 'Registration failed. Please try again.', 'error');
                }
            }
        } catch (error) {
            swal.close();
            swal('Error', 'An error occurred. Please check your connection and try again.', 'error');
            console.error('Signup error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="react-login-page react-signup-page pt---120 pb---120">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="login-right-form">
                                <form onSubmit={handleSubmit}>
                                    <div className="login-top">
                                        <h3>Sign Up</h3>
                                        <p>Already have an account? <Link to="/login">Log in</Link></p>
                                    </div>

                                    <p>
                                        <label>Access Code</label>
                                        {collegeName ? (
                                            <div style={{ fontWeight: 700, marginBottom: '8px' }}>{collegeName}</div>
                                        ) : null}
                                        <input
                                            placeholder="Enter access code"
                                            type="text"
                                            id="collegeCode"
                                            name="collegeCode"
                                            value={formData.collegeCode}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        />
                                        {isCollegeLookupLoading ? <span className="error-text" style={{ color: '#4D5765' }}>Checking college code...</span> : null}
                                        {!isCollegeLookupLoading && collegeLookupError ? <span className="error-text">{collegeLookupError}</span> : null}
                                        {errors.collegeCode && <span className="error-text">{errors.collegeCode}</span>}
                                    </p>

                                    <p>
                                        <label>Email</label>
                                        <input 
                                            placeholder="Email" 
                                            type="email" 
                                            id="email" 
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        />
                                        {errors.email && <span className="error-text">{errors.email}</span>}
                                    </p>

                                    <p>
                                        <label>Full Name</label>
                                        <input 
                                            placeholder="Full Name" 
                                            type="text" 
                                            id="name" 
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        />
                                        {errors.name && <span className="error-text">{errors.name}</span>}
                                    </p>

                                    <p>
                                        <label>Phone (Optional)</label>
                                        <input 
                                            placeholder="Phone Number" 
                                            type="tel" 
                                            id="phone" 
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        />
                                    </p>

                                    <p>
                                        <label>Password</label>
                                        <div className="password-input-wrapper">
                                            <input 
                                                placeholder="Password" 
                                                type={showPassword ? 'text' : 'password'} 
                                                id="password" 
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                disabled={isSubmitting}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <path d="M1.5 12C3.9 7.8 7.6 5.7 12 5.7C16.4 5.7 20.1 7.8 22.5 12C20.1 16.2 16.4 18.3 12 18.3C7.6 18.3 3.9 16.2 1.5 12Z" stroke="currentColor" strokeWidth="1.8"/>
                                                    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {errors.password && <span className="error-text">{errors.password}</span>}
                                    </p>

                                    <p>
                                        <label>Confirm Password</label>
                                        <div className="password-input-wrapper">
                                            <input 
                                                placeholder="Confirm Password" 
                                                type={showConfirmPassword ? 'text' : 'password'} 
                                                id="password2" 
                                                name="password2"
                                                value={formData.password2}
                                                onChange={handleInputChange}
                                                disabled={isSubmitting}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                disabled={isSubmitting}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <path d="M1.5 12C3.9 7.8 7.6 5.7 12 5.7C16.4 5.7 20.1 7.8 22.5 12C20.1 16.2 16.4 18.3 12 18.3C7.6 18.3 3.9 16.2 1.5 12Z" stroke="currentColor" strokeWidth="1.8"/>
                                                    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {errors.password2 && <span className="error-text">{errors.password2}</span>}
                                    </p>

                                    <div className="back-check-box">
                                        <input 
                                            type="checkbox" 
                                            id="agreeToTerms" 
                                            name="agreeToTerms"
                                            checked={formData.agreeToTerms}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        /> 
                                        <label htmlFor="agreeToTerms" className="terms-label">
                                            I agree to the <em>Terms & Conditions</em>
                                        </label>
                                        {errors.agreeToTerms && <span className="error-text d-block">{errors.agreeToTerms}</span>}
                                    </div>

                                    <button 
                                        type="submit" 
                                        id="button" 
                                        name="submit"
                                        disabled={isSubmitting}
                                    >
                                        Register <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-right"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>  
        </>
    );
}

export default SignupMain;